/**
 * The process tab's EPHEMERAL lane draft → laned BPMN layout.
 *
 * The process diagram tab is conceptually a synthesized `view { flow <proc> }`
 * (flowData.ts). This module lets that view carry an editable `bpmn { lane/pool }`
 * body held browser-only (a `FlowBodyRow[]`): it applies the SAME lane machinery
 * the on-board flow view uses — `resolveBpmnLanes` → `assignBpmnLanes` — as a
 * post-pass over the tab's LIVE-walk layout, so playback (active step / branch
 * dim, all threaded off `stepIndex`) is preserved (`assignBpmnLanes` only remaps
 * y). Nothing is written to the model until the user saves the draft as a view.
 */
import {
  assignBpmnLanes,
  parseViewSource,
  performersOf,
  resolveBpmnLanes,
  type BpmnLayout,
  type EvalContext,
  type FlowBodyClause,
} from "@archlang/engine";
import { flowBodyRowText, type FlowBodyRow } from "../views/clauseModel.js";

/** Parse a draft lane-body (clause-text lines) into `FlowBodyClause[]`. The flow
 *  ref is a throwaway (`P`) — only the body clauses matter to the lane plan.
 *  Fail-soft: a malformed body yields no clauses (→ no lanes), never a throw. */
export function parseDraftLaneBody(rows: readonly FlowBodyRow[]): readonly FlowBodyClause[] {
  const lines = rows.map(flowBodyRowText).filter((t): t is string => t !== null);
  if (lines.length === 0) return [];
  try {
    const { decl, errors } = parseViewSource(`view V { flow P bpmn { ${lines.join("; ")} } }`);
    if (errors.length > 0) return [];
    const fc = decl.clauses.find((c) => c.kind === "flow") as { readonly body?: readonly FlowBodyClause[] } | undefined;
    return fc?.body ?? [];
  } catch {
    return [];
  }
}

/**
 * Apply an ephemeral lane draft to a live BPMN layout. Returns the layout
 * unchanged when the draft is empty or resolves to no lane plan. `universe` (the
 * lane-selector node universe, `buildNodeUniverse(model)`) is passed in so the
 * caller memoizes that full-model pass on the model, not per keystroke.
 */
export function applyDraftLanes(
  layout: BpmnLayout,
  body: readonly FlowBodyClause[],
  universe: EvalContext["universe"],
): BpmnLayout {
  if (body.length === 0) return layout;
  try {
    const plan = resolveBpmnLanes(body, performersOf(layout), { universe, knobs: new Map() });
    return assignBpmnLanes(layout, plan);
  } catch {
    return layout;
  }
}
